export const normalizeQuantityDraft = (value: string | number): string => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return ''
    return String(Math.max(0, Math.trunc(value)))
  }

  return String(value || '').replace(/\D/g, '')
}

/**
 * Keep only digits and an optional single leading minus, preserving a lone "-"
 * so the user can type the sign before the number. For signed quantity-change
 * drafts (e.g. stock add/remove). Never clamps — validation happens on commit.
 */
export const normalizeSignedIntegerDraft = (value: string | number): string => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return ''
    return String(Math.trunc(value))
  }

  const raw = String(value ?? '')
  const negative = raw.trim().startsWith('-')
  const digits = raw.replace(/\D/g, '')
  if (!digits) return negative ? '-' : ''
  return `${negative ? '-' : ''}${digits}`
}

export const parseQuantityDraft = (
  draft: string | number,
  min = 1,
  max?: number | null,
): number | null => {
  const normalized = normalizeQuantityDraft(draft)
  if (!normalized) return null

  const parsed = Number(normalized)
  if (!Number.isInteger(parsed)) return null
  if (parsed < min) return null
  if (typeof max === 'number' && Number.isFinite(max) && parsed > max) return null

  return parsed
}

export const stepQuantityDraft = (
  draft: string | number,
  delta: number,
  min = 1,
  max?: number | null,
): string => {
  const base = parseQuantityDraft(draft, min, max) ?? min
  const next = Math.max(min, base + delta)
  if (typeof max === 'number' && Number.isFinite(max)) {
    return String(Math.min(max, next))
  }
  return String(next)
}

export const getQuantityRangeWarning = (min = 1, max?: number | null): string => {
  if (typeof max === 'number' && Number.isFinite(max)) {
    return `You will not be able to add to inventory until quantity is between ${min} and ${max}.`
  }

  return `You will not be able to add to inventory until quantity is at least ${min}.`
}